import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

// A "site" within an organization (e.g. Fumba Town, Soul). Owners/tenants are scoped to one
// site; admins/super-admins can switch between them or see "All Zanzibar" (no filter).
export interface SiteLocation {
  id: string;
  name: string;
  organization_id: string;
}

interface LocationContextType {
  locations: SiteLocation[];
  selectedLocationId: string | null;            // null = "All Zanzibar" (no site filter)
  setSelectedLocationId: (id: string | null) => void;
  selectedLocationName: string;                 // for display
  canSwitch: boolean;                           // admin/super-admin with at least one site
  loading: boolean;
  refreshLocations: () => Promise<void>;        // re-fetch the site list (after add/rename)
  // Site scope, resolved ONCE in the provider and cached, so pages get it synchronously on mount
  // (no per-page flash). null = "All Zanzibar" (no filter). [] = site has no buildings/meters.
  scopeBuildingIds: string[] | null;
  scopeMeterIds: string[] | null;
  scopeLoading: boolean;
}

const LS_KEY = 'sme_selected_location';
const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const useLocation = (): LocationContextType => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation must be used within a LocationProvider');
  return ctx;
};

// Pages keyed by building_id (meters, apartments, readings…). Reads the provider-cached scope,
// so navigating between pages keeps the same site without an async re-resolve / Fumba flash.
export const useLocationBuildingIds = (): { buildingIds: string[] | null; loading: boolean; selectedLocationId: string | null } => {
  const { scopeBuildingIds, scopeLoading, selectedLocationId } = useLocation();
  return { buildingIds: scopeBuildingIds, loading: scopeLoading, selectedLocationId };
};

// Transactional pages keyed by meter_id (payments, top-ups, reloads, financial/activity logs).
// null = "All Zanzibar"; [] = site has no meters (show nothing).
export const useLocationMeterIds = (): { meterIds: string[] | null; loading: boolean; selectedLocationId: string | null } => {
  const { scopeMeterIds, scopeLoading, selectedLocationId } = useLocation();
  return { meterIds: scopeMeterIds, loading: scopeLoading, selectedLocationId };
};

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, userRole, isPlatformAdmin } = useAuth();
  const isAdmin = userRole === 'admin' || isPlatformAdmin;

  const [locations, setLocations] = useState<SiteLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocationId, setSel] = useState<string | null>(() => {
    const v = localStorage.getItem(LS_KEY);
    return v && v !== 'all' ? v : null;
  });

  // Provider-level scope cache (building + meter ids for the selected site)
  const [scopeBuildingIds, setScopeBuildingIds] = useState<string[] | null>(null);
  const [scopeMeterIds, setScopeMeterIds] = useState<string[] | null>(null);
  const [scopeLoading, setScopeLoading] = useState(false);

  const setSelectedLocationId = useCallback((id: string | null) => {
    setSel(id);
    localStorage.setItem(LS_KEY, id ?? 'all');
  }, []);

  // Fetch the sites the current admin may switch between. Exposed via `refreshLocations`
  // so the settings page can re-pull after adding/renaming a site.
  const fetchLocations = useCallback(async () => {
    // Only admins/super-admins switch sites; everyone else is hard-scoped by RLS.
    if (!user || !isAdmin) {
      setLocations([]);
      return;
    }
    setLoading(true);
    try {
      // `locations` is not in the generated types yet → cast.
      let query = (supabase as any)
        .from('locations')
        .select('id, name, organization_id')
        .order('name');
      // Scope the switcher to the CURRENT org for everyone (incl. super-admins, who operate
      // within a selected org). RLS would otherwise hand platform admins every org's sites,
      // and a cross-org site can't match the org-scoped Buildings query → empty view.
      const orgId = localStorage.getItem('organizationId');
      if (orgId) query = query.eq('organization_id', orgId);
      const { data, error } = await query;
      if (error) { setLocations([]); return; }
      const locs = (data || []) as SiteLocation[];
      setLocations(locs);
      // If the persisted selection is no longer valid, fall back to "All Zanzibar".
      if (selectedLocationId && !locs.some(l => l.id === selectedLocationId)) {
        setSelectedLocationId(null);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userRole, isPlatformAdmin]);

  // Load the sites the current admin may switch between
  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  // Resolve the selected site's building + meter ids ONCE, cached here. Pages read this
  // synchronously so switching pages doesn't briefly show the wrong site's data.
  useEffect(() => {
    if (!selectedLocationId) {
      setScopeBuildingIds(null);
      setScopeMeterIds(null);
      setScopeLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setScopeLoading(true);
      try {
        const { data: blds } = await (supabase as any)
          .from('buildings').select('id').eq('location_id', selectedLocationId);
        const bids = (blds || []).map((b: { id: string }) => b.id);
        if (cancelled) return;
        setScopeBuildingIds(bids);
        if (!bids.length) { setScopeMeterIds([]); return; }
        const { data: ms } = await supabase.from('smart_meters').select('id').in('building_id', bids);
        if (!cancelled) setScopeMeterIds((ms || []).map((m: { id: string }) => m.id));
      } finally {
        if (!cancelled) setScopeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLocationId]);

  const canSwitch = !!user && isAdmin && locations.length > 0;
  const selectedLocationName = selectedLocationId
    ? (locations.find(l => l.id === selectedLocationId)?.name || 'Site')
    : 'All Zanzibar';

  return (
    <LocationContext.Provider
      value={{
        locations, selectedLocationId, setSelectedLocationId, selectedLocationName, canSwitch, loading,
        refreshLocations: fetchLocations,
        scopeBuildingIds, scopeMeterIds, scopeLoading,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
